import React, { useRef } from "react";
import { useObjectUrl } from "../../../utils/videoPreviewUrl";
import { Box, Button, FormHelperText } from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ClearIcon from "@mui/icons-material/Clear";
import { toast } from "react-toastify";
import {
  dressVideoAccept,
  isCompetitionVideoFile,
} from "../../../utils/competitionEventMedia";
import { MAX_DRESS_VIDEO_SEC, probeClientDuration } from "../../../utils/videoProbe";

const PromoVideoUpload = ({
  formik,
  edited,
  fieldName = "video",
  label = "Upload Promotional Video",
}) => {
  const inputRef = useRef(null);
  const videoValue = formik.values[fieldName];
  const filePreview = useObjectUrl(videoValue instanceof File ? videoValue : null);
  const preview =
    typeof videoValue === "string" && videoValue
      ? videoValue
      : filePreview || null;

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isCompetitionVideoFile(file)) {
      toast.error("Please select a valid video file (MP4, MOV, WEBM, etc.)");
      return;
    }
    const duration = await probeClientDuration(file);
    if (duration == null) {
      toast.error(
        "Could not read this video. Use a real recording (MP4 or MOV from your phone works best)."
      );
      return;
    }
    if (duration > MAX_DRESS_VIDEO_SEC) {
      toast.error("Video must be 10 minutes or less");
      return;
    }
    formik.setFieldValue(fieldName, file);
  };

  return (
    <Box>
      <input
        type="file"
        accept={dressVideoAccept}
        ref={inputRef}
        style={{ display: "none" }}
        onChange={handleChange}
      />
      {preview ? (
        <Box
          sx={{
            position: "relative",
            maxWidth: 480,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #e5e7eb",
            background: "#0f1220",
          }}
        >
          <Box
            component="video"
            key={preview}
            src={preview}
            controls
            playsInline
            preload="metadata"
            sx={{ width: "100%", maxHeight: 260, display: "block", bgcolor: "#000" }}
          />
          <Box sx={{ p: 1.2, display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => formik.setFieldValue(fieldName, "")}
            >
              Remove
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<FileUploadIcon />}
              onClick={() => inputRef.current?.click()}
              sx={{ background: "#ef364e", "&:hover": { background: "#d32d43" } }}
            >
              {edited ? "Replace" : "Change"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          onClick={() => inputRef.current?.click()}
          sx={{
            border: "1.5px dashed #c5c9d6",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            background: "#fafbfc",
            transition: "0.2s",
            "&:hover": { borderColor: "#ef364e", background: "#fff5f6" },
          }}
        >
          <FileUploadIcon sx={{ fontSize: 36, color: "#ef364e", mb: 1 }} />
          <Box sx={{ fontWeight: 600, color: "#222" }}>{label}</Box>
          <Box sx={{ fontSize: 13, color: "#777", mt: 0.5 }}>
            MP4, MOV, WEBM · up to 10 minutes (same as pageant uploads)
          </Box>
        </Box>
      )}
      {formik.touched[fieldName] && formik.errors[fieldName] ? (
        <FormHelperText sx={{ color: "red" }}>{formik.errors[fieldName]}</FormHelperText>
      ) : null}
    </Box>
  );
};

export default PromoVideoUpload;
